import { Component, ViewChild, ElementRef, AfterViewInit, Output, EventEmitter, Input, HostListener } from '@angular/core';
import { NgbDate, NgbCalendar, NgbDateParserFormatter, NgbDatepicker, NgbDatepickerI18n } from '@ng-bootstrap/ng-bootstrap';
import { I18n, CustomDatepickerI18nService } from '../../services/custom-datepicker-i18n.service';
import { CustomDatepickerFormaterService } from '../../services/custom-datepicker-formater.service';
import { IDateRangePicker } from '../../interfaces/check-filter';

@Component({
  selector: 'app-range-datepicker',
  templateUrl: './range-datepicker.component.html',
  styleUrls: ['./range-datepicker.component.scss'],
  providers: [
    I18n,
    { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18nService },
    { provide: NgbDateParserFormatter, useClass: CustomDatepickerFormaterService },
  ],
})
export class RangeDatepickerComponent implements AfterViewInit {
  @ViewChild('datepickerTrigger', { read: ElementRef }) datepickerTrigger: ElementRef;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const clickedInside =
      this.datepickerContainer?.nativeElement.contains(target) || this.datepickerTrigger?.nativeElement.contains(target);

    if (!clickedInside && this.toggledCalendar) {
      this.toggleCalendar(true); // 👮🏽 Only close if the calendar is open
    }
  }

  @ViewChild('datepickerContainer', { read: ElementRef, static: false })
  public datepickerContainer: ElementRef;

  @ViewChild('dp', { static: false })
  public datepicker: NgbDatepicker;

  @Output()
  public changeDates: EventEmitter<any>;

  @Input()
  public set daterangeOptions(daterangeOptions: IDateRangePicker) {
    if (daterangeOptions !== null && daterangeOptions !== undefined) {
      this.dateRangePicker = daterangeOptions;
      this.resetCalendar(daterangeOptions);
    }
  }

  public hoveredDate: NgbDate;

  public displayedFromDate: string;
  public displayedToDate: string;
  public fromDate: NgbDate;
  public toDate: NgbDate;
  public minDate: NgbDate;
  public maxDate: NgbDate;
  public toggledCalendar: boolean;

  private dateRangePicker: IDateRangePicker;

  constructor(private calendar: NgbCalendar) {
    this.changeDates = new EventEmitter<any>();
    this.toggledCalendar = false;
  }

  /**
   * Function on after view init
   */
  public ngAfterViewInit(): void {
    this.datepicker.navigateTo(this.fromDate);
  }

  /**
   * Function triggered when date selection input changes
   *
   * @param date Selected date
   */
  public onDateSelection(date: NgbDate): void {
    const rangeDays = this.createRangeDays(this.dateRangePicker);
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.toDate = null; // Ensure its empty
      this.maxDate = this.calendar.getNext(date, 'd', rangeDays);
    } else if (this.fromDate && !this.toDate && (date.after(this.fromDate) || date.equals(this.fromDate))) {
      this.toDate = date;
      this.maxDate = this.calendar.getToday();
      this.toggleCalendar();
    } else {
      this.fromDate = date;
      this.toDate = null;
      this.maxDate = this.calendar.getNext(date, 'd', rangeDays);
    }

    if (this.maxDate.after(this.calendar.getToday())) {
      this.maxDate = this.calendar.getToday();
    }

    this.createDisplayedDate();

    // const toEmitToDate = this.toDate && this.toDate !== this.fromDate ? this.toDate : null;
    this.changeDates.emit({ from: this.fromDate, to: this.toDate });
  }

  /**
   * Function to set hovered class in day with mouseover
   *
   * @param date Date with mouseover
   */
  public isHovered(date: NgbDate): boolean {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  /**
   * Function to set 'inside' class to day inside between fromDate and toDate
   *
   * @param date Date inside date range
   */
  public isInside(date: NgbDate): boolean {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  /**
   * Function to set 'range' class to day in range
   *
   * @param date Date in range
   */
  public isRange(date: NgbDate): boolean {
    return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
  }

  /**
   * Function to set 'out-ranged' class to day in range
   *
   * @param date Date out ranged
   */
  public isOutRanged(date: NgbDate): boolean {
    return date.after(this.maxDate) || date.before(this.minDate);
  }

  /**
   * Function to check if the curreent date is last date picked
   *
   * @param date Current date
   */
  public isLastDate(date: NgbDate): boolean {
    return date.equals(this.toDate);
  }

  /**
   * Function to check if the curreent date is first date picked
   *
   * @param date Current date
   */
  public isFirstDate(date: NgbDate): boolean {
    return date.equals(this.fromDate);
  }

  /**
   * Function to open or close calendar
   */
  public toggleCalendar(force?: boolean): void {
    const { style } = this.datepickerContainer.nativeElement;

    // To close from filter-date component
    if (force) {
      if (style.display === 'block') {
        style.display = 'none';
        this.toggledCalendar = false;
      }
      return; // 👈 dont open from another component if its closed
    }

    if (style.display === 'block' || force) {
      style.display = 'none';
      this.toggledCalendar = false;
    } else {
      style.display = 'block';
      this.toggledCalendar = true;

      // 👇🏽 Navigate to current Date
      if (this.fromDate && this.datepicker) {
        setTimeout(() => {
          this.datepicker.navigateTo(this.fromDate);
        });
      }

      // ⚠️ recalculate maxDate if fromDate is set to avoid enable a date out of range
      if (this.fromDate) {
        const rangeDays = this.createRangeDays(this.dateRangePicker);

        // 🔄️ if selected toDate, allow from toDate to todayDate
        if (this.toDate) {
          this.maxDate = this.calendar.getToday();
        } else {
          const tentativeMax = this.calendar.getNext(this.fromDate, 'd', rangeDays);
          this.maxDate = tentativeMax.after(this.calendar.getToday()) ? this.calendar.getToday() : tentativeMax;
        }
      } else {
        this.maxDate = this.calendar.getToday();
      }
    }
  }

  /**
   * Function to create displayed date string
   */
  private createDisplayedDate(): void {
    if (this.fromDate) {
      let fromDate = this.fromDate.day < 10 ? `0${this.fromDate.day}/` : `${this.fromDate.day}/`;
      fromDate += this.fromDate.month < 10 ? `0${this.fromDate.month}/` : `${this.fromDate.month}/`;
      fromDate += `${this.fromDate.year}`;
      this.displayedFromDate = fromDate;
    }

    if (this.toDate) {
      let toDate = this.toDate.day < 10 ? `0${this.toDate.day}/` : `${this.toDate.day}/`;
      toDate += this.toDate.month < 10 ? `0${this.toDate.month}/` : `${this.toDate.month}/`;
      toDate += `${this.toDate.year}`;
      this.displayedToDate = toDate;
    } else {
      this.displayedToDate = '';
    }
  }

  /**
   * Function to create a range days
   */
  private createRangeDays(daterangeOptions: IDateRangePicker): number {
    let rangeDays = 30;
    if (daterangeOptions.daysRange) {
      rangeDays = daterangeOptions.daysRange;
    } else if (daterangeOptions.weeksRange) {
      rangeDays = daterangeOptions.weeksRange * 7;
    } else if (daterangeOptions.monthsRange) {
      rangeDays = daterangeOptions.monthsRange * 30;
    }

    return rangeDays;
  }

  /**
   * Reset dates selected
   */
  public resetCalendar(daterangeOptions?: IDateRangePicker): void {
    if (daterangeOptions.minDayRange) {
      if (daterangeOptions.minDayRange === 1) {
        this.fromDate = this.calendar.getToday();
      } else {
        this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', daterangeOptions.minDayRange);
      }
    } else {
      this.fromDate = this.calendar.getPrev(this.calendar.getToday(), 'd', 7);
    }
    this.toDate = this.calendar.getToday();
    this.minDate = this.calendar.getPrev(this.calendar.getToday(), 'm', this.dateRangePicker.minMonth || 6);
    this.maxDate = this.calendar.getToday();
    this.createDisplayedDate();

    this.changeDates.emit({ from: this.fromDate, to: this.toDate });
  }
}
